import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type SarvamVoiceAiSettings,
} from "@/api/hospitalAdmin";
import { toast } from "sonner";
import { CRM_LEAD_SOURCES } from "@/lib/leadSources";

const REMINDER_PURPOSE = "APPOINTMENT_REMINDER";

type Props = {
  settings: SarvamVoiceAiSettings | undefined;
  isLoading: boolean;
};

export function SarvamAppointmentReminderSettingsCard({ settings, isLoading }: Props) {
  const qc = useQueryClient();
  const reminderAgents = useMemo(
    () =>
      (settings?.agents ?? []).filter(
        (a) => (a.purpose ?? "").trim() === REMINDER_PURPOSE || a.name.toLowerCase().includes("reminder")
      ),
    [settings?.agents]
  );
  const primaryReminder = reminderAgents[0];

  const [enabled, setEnabled] = useState(false);
  const [rolloutMode, setRolloutMode] = useState<"TEST" | "LIVE">("TEST");
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [agentAppId, setAgentAppId] = useState("");
  const [agentVersion, setAgentVersion] = useState("1");
  const [connectionId, setConnectionId] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [agentName, setAgentName] = useState("Appointment Reminder Agent");
  const [agentEnabled, setAgentEnabled] = useState(true);

  useEffect(() => {
    if (!settings) return;
    setEnabled(!!(settings as SarvamVoiceAiSettings & { appointmentReminderEnabled?: boolean })
      .appointmentReminderEnabled);
    const ext = settings as SarvamVoiceAiSettings & {
      appointmentReminderRolloutMode?: "TEST" | "LIVE";
      appointmentReminderAgentAppId?: string | null;
      appointmentReminderLeadSourcesAllowlist?: string[];
    };
    setRolloutMode(ext.appointmentReminderRolloutMode === "LIVE" ? "LIVE" : "TEST");
    setLeadSources(ext.appointmentReminderLeadSourcesAllowlist ?? []);
    const appId =
      ext.appointmentReminderAgentAppId?.trim() ||
      primaryReminder?.sarvamAppId ||
      "";
    setAgentAppId(appId);
    if (primaryReminder) {
      setAgentVersion(primaryReminder.sarvamAppVersion ?? "1");
      setConnectionId(primaryReminder.sarvamConnectionId ?? "");
      setAgentPhone(primaryReminder.agentPhoneNumber ?? "");
      setAgentName(primaryReminder.name || "Appointment Reminder Agent");
      setAgentEnabled(primaryReminder.enabled);
    }
  }, [settings, primaryReminder]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const appId = agentAppId.trim();
      if (enabled && !appId) {
        throw new Error("Agent App ID is required when reminders are enabled");
      }
      await hospitalAdminApi.saveSarvamVoiceAiSettings({
        appointmentReminderEnabled: enabled,
        appointmentReminderRolloutMode: rolloutMode,
        appointmentReminderLeadSourcesAllowlist: leadSources,
        appointmentReminderAgentAppId: appId || undefined,
        agent: {
          id: primaryReminder?.id || undefined,
          sarvamAppId: appId,
          sarvamAppVersion: agentVersion.trim() || "1",
          sarvamConnectionId: connectionId.trim(),
          agentPhoneNumber: agentPhone.trim(),
          name: agentName.trim() || "Appointment Reminder Agent",
          purpose: REMINDER_PURPOSE,
          enabled: agentEnabled,
        },
      });
    },
    onSuccess: async () => {
      toast.success("Appointment reminder settings saved");
      await qc.invalidateQueries({ queryKey: ["sarvam-voice-ai-settings"] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to save";
      toast.error(message);
    },
  });

  const toggleSource = (value: string, checked: boolean) => {
    setLeadSources((prev) =>
      checked ? [...new Set([...prev, value])] : prev.filter((s) => s !== value)
    );
  };

  const inputContract =
    (settings as SarvamVoiceAiSettings & { appointmentReminderInputVariableContract?: string[] })
      ?.appointmentReminderInputVariableContract ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voice AI — OPD appointment reminder (~2 hours before)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Separate from the CRM new-lead outbound agent. When enabled, SetuOS queues a Sarvam voice call
          about <strong>2 hours before</strong> the OPD consultation time (IST). WhatsApp reminders stay at
          ~3 hours. Requires master Sarvam integration ON, API key, and{" "}
          <code className="text-xs">SARVAM_VOICE_WORKER_ENABLED</code> on the server.
        </p>

        <div className="flex items-start gap-3 rounded-lg border p-3">
          <Checkbox
            checked={enabled}
            onCheckedChange={(v) => setEnabled(v === true)}
            disabled={isLoading || saveMutation.isPending}
          />
          <div>
            <Label className="font-medium">Appointment reminder calls ON</Label>
            <div className="text-xs text-muted-foreground mt-1">
              {settings?.enabled
                ? "Chain Sarvam integration is on."
                : "Turn on master Sarvam integration (Overview) first."}
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-sm">Rollout</Label>
            <Select value={rolloutMode} onValueChange={(v: "TEST" | "LIVE") => setRolloutMode(v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TEST">Test — only patients whose name contains &quot;test&quot;</SelectItem>
                <SelectItem value="LIVE">Live — all OPD appointments (subject to lead source filter below)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2 border-t pt-4">
          <Label className="font-medium">Lead sources to call</Label>
          <p className="text-xs text-muted-foreground">
            Optional. Leave all unchecked to remind every OPD appointment. Check sources to place
            reminder calls only when the linked CRM lead (or appointment booking source) matches.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 max-h-48 overflow-y-auto border rounded-md p-3">
            {CRM_LEAD_SOURCES.map((s) => (
              <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={leadSources.includes(s.value)}
                  onCheckedChange={(v) => toggleSource(s.value, v === true)}
                  disabled={isLoading || saveMutation.isPending}
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2 border-t pt-4">
          <Label className="font-medium">Reminder Sarvam agent</Label>
          <p className="text-xs text-muted-foreground">
            Create this agent in Sarvam with the same webhook URL as CRM outbound. Purpose must be{" "}
            <code className="text-xs">APPOINTMENT_REMINDER</code>.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              placeholder="Sarvam Agent App ID"
              value={agentAppId}
              onChange={(e) => setAgentAppId(e.target.value)}
            />
            <Input
              placeholder="App version"
              value={agentVersion}
              onChange={(e) => setAgentVersion(e.target.value)}
            />
            <Input
              placeholder="Connection ID"
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
            />
            <Input
              placeholder="Agent phone (CLI)"
              value={agentPhone}
              onChange={(e) => setAgentPhone(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={agentEnabled} onCheckedChange={(v) => setAgentEnabled(v === true)} />
            Agent enabled
          </label>
        </div>

        {inputContract.length > 0 ? (
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Sarvam inputs: </span>
            {inputContract.join(", ")}
          </div>
        ) : null}

        <div className="flex justify-end border-t pt-4">
          <Button onClick={() => saveMutation.mutate()} disabled={isLoading || saveMutation.isPending}>
            {saveMutation.isPending ? "Saving…" : "Save appointment reminder"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
